import React, { useCallback, useEffect, useRef } from 'react';

import './Die.css';


// Must match the tumble-to-N animation duration in Die.css
// (1100ms ... forwards). The settle fallback is armed at this duration
// plus a small margin so a real `animationend` wins whenever the tab can
// deliver it; the timer only takes over when the event is dropped.
const TUMBLE_ANIMATION_MS = 1100;

// Belt-and-suspenders settle. A backgrounded / blurred tab can throttle
// or never deliver `animationend` — the focused roller fires it, but an
// observer window sitting behind it may not. Without a fallback the
// overlay strands at phase 'tumbling' forever, because the dice overlays'
// watchdog only dismisses while still 'idle' (a never-settled tumble is
// never cleared — the "dice modal never clears on non-host" report).
// setTimeout still fires on a hidden tab, so arm one a beat past the
// animation duration to guarantee onSettled lands. Idempotent via
// settledRef, so a late `animationend` after the fallback never double-fires.
const SETTLE_FALLBACK_MS = TUMBLE_ANIMATION_MS + 250;


function detectReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}


export default function Die({
        faces, faceLabels, result, onSettled, reduced, animating }) {
    const shouldAnimate = animating !== false;  // default true
    const isReduced = (reduced !== undefined)
        ? !!reduced
        : detectReducedMotion();
    const settledRef = useRef(false);
    const onSettledRef = useRef(onSettled);
    const fallbackTimerRef = useRef(null);

    useEffect(() => {
        onSettledRef.current = onSettled;
    }, [onSettled]);

    // Single settle gate. Whichever path arrives first — CSS animationend,
    // the reduced-motion mount effect, or the duration fallback — fires
    // onSettled exactly once and disarms the fallback timer.
    const fireSettled = useCallback(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
        const fn = onSettledRef.current;
        if (fn) fn();
    }, []);

    // Reduced-motion path: no tumble animation runs, so animationend never
    // fires. Settle synchronously on mount.
    useEffect(() => {
        if (!shouldAnimate) return;
        if (!isReduced) return;
        fireSettled();
    }, [isReduced, shouldAnimate, fireSettled]);

    // Animated path: settle on the CSS animationend (handleAnimationEnd
    // below), with a duration-matched setTimeout fallback for tabs that
    // never deliver the event (see SETTLE_FALLBACK_MS).
    useEffect(() => {
        if (!shouldAnimate) return undefined;
        if (isReduced) return undefined;
        fallbackTimerRef.current = setTimeout(fireSettled, SETTLE_FALLBACK_MS);
        return () => {
            if (fallbackTimerRef.current) {
                clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
        };
    }, [isReduced, shouldAnimate, fireSettled]);

    const handleAnimationEnd = () => {
        if (!shouldAnimate) return;
        if (isReduced) return;
        fireSettled();
    };

    const dieClasses = ['die'];
    if (shouldAnimate) {
        dieClasses.push(`die--result-${result}`);
        if (isReduced) dieClasses.push('die--reduced');
    } else {
        dieClasses.push('die--idle');
    }

    const safeFaces = Array.isArray(faces) ? faces : [];
    const safeLabels = Array.isArray(faceLabels) ? faceLabels : [];

    // Always render 6 face panels so the cube body is solid even when
    // a face has no artwork (blank faces on the Fate Die).
    const facePanels = Array.from({ length: 6 }).map((_, i) => {
        const url = safeFaces[i] || null;
        const label = safeLabels[i] || '';
        return (
            <div
                key={i}
                className={`die-face die-face--${i + 1}`}
                aria-label={label || undefined}
            >
                {url ? (
                    <img
                        className={'die-face__art'}
                        src={url}
                        alt={label}
                        draggable={false}
                    />
                ) : null}
            </div>
        );
    });

    return (
        <div className={'die-stage'}>
            <div
                className={dieClasses.join(' ')}
                onAnimationEnd={shouldAnimate ? handleAnimationEnd : undefined}
                data-testid={'die'}
                data-result={result}
                data-animating={shouldAnimate ? 'true' : 'false'}
            >
                {facePanels}
            </div>
        </div>
    );
}
